import type { HsCamera } from '../engine-new/objects/cameras/camera.class';
import { HsGeometryType } from '../graphic-engine-new/geometry/geometry-type.enum';
import { HsIndexedGeometry } from '../graphic-engine-new/geometry/indexed-geometry.class';
import type { HsGeometry } from '../graphic-engine-new/geometry/geometry.interface';
import { HsTexturedIndexedGeometry } from '../graphic-engine-new/geometry/textured-indexed-geometry.class';
import { HsGraphicObject } from './objects/graphic-object.class';
import { HsDefaultShader, type HsMeshBatchMode } from './shaders/classes/default-shader.class';
import { HsShaderLightFunctions } from './shaders/functions/shader-light-functions.function';
import type { HsShader } from './shaders/classes/shader.class';
import {
  HS_MESH_BATCH_OBJECTS_PER_ROW,
  HS_MESH_BATCH_PIXELS_PER_OBJECT,
  HS_MESH_BATCH_WINDOW_OBJECTS,
} from './constants/mesh-batch-layout.constant';

interface MultiDrawExtension {
  multiDrawElementsWEBGL(
    mode: number,
    counts: Int32Array,
    countsOffset: number,
    type: number,
    offsets: Int32Array,
    offsetsOffset: number,
    drawCount: number
  ): void;
}

interface GeometryPage {
  positions: WebGLBuffer;
  normals: WebGLBuffer;
  indices: WebGLBuffer;
  verticesUsed: number;
  indicesUsed: number;
}

interface GeometryRange {
  page: GeometryPage;
  offset: number;
  vertices: number;
  usedAt: number;
}

interface ShaderSignature {
  id: number;
  clippingStorage: HsDefaultShader['clippingStorage'];
  lightStorage: HsDefaultShader['lightStorage'];
  vertexCode: () => string;
  fragmentCode: () => string;
}

interface TransformWindow {
  objects: HsGraphicObject[];
  values: Float64Array;
  camera: number[];
  texture: WebGLTexture;
}

export interface HsMeshBatch {
  count: number;
  mode: HsMeshBatchMode;
  shader: HsDefaultShader;
  page: GeometryPage | null;
}

const MAX_OBJECTS: number = 64;
const MAX_INDICES: number = 30000;
const PAGE_VERTICES: number = 32768;
const PAGE_INDICES: number = 65536;
const PAGE_BYTES: number = PAGE_VERTICES * 24 + PAGE_INDICES * 4;
const MAX_ATLAS_BYTES: number = 64 * 1024 * 1024;
const MAX_DATA_TEXTURES: number = 64;
// Packed ranges are append-only, so a full atlas is rebuilt once enough of it is disposed or no longer drawn.
const MIN_RECLAIMABLE_ATLAS_FRACTION: number = 0.25;
const MIN_COMPACTION_INTERVAL_MS: number = 1000;
const UNUSED_RANGE_MS: number = 2000;
// Small atlases are cheap to keep; only rebuild a partly filled atlas once it holds a meaningful amount of stale data.
const MIN_SWEEP_REBUILD_PAGES: number = 8;
const MIN_SWEEP_RECLAIMABLE_FRACTION: number = 0.5;
// Geometry packed for earlier lists lands on other pages than its new neighbours and splits their batches.
// Shared geometry splits batches even in render-order packing, so splits are compared with the rate measured
// right after the last render-order rebuild (or, before one, with a few splits per page).
const MIN_SWEEP_PAGE_SPLITS: number = 64;
const SWEEP_PAGE_SPLITS_PER_PAGE: number = 4;
const SWEEP_PAGE_SPLIT_GROWTH: number = 2;
// Bounds the drain loop: a lost context keeps reporting CONTEXT_LOST_WEBGL.
const MAX_PENDING_GL_ERRORS: number = 16;

export class HsMeshBatchRenderer {
  public revision: number = 0;
  public reuseTransformData: boolean = false;
  private readonly extension: MultiDrawExtension | null;
  private readonly pages: GeometryPage[] = [];
  private ranges: WeakMap<HsIndexedGeometry, GeometryRange> = new WeakMap();
  private readonly packedGeometries: HsIndexedGeometry[] = [];
  private compactionRequested: boolean = false;
  private lastCompactionCheckAt: number = Number.NEGATIVE_INFINITY;
  private sweepStartedAt: number = Number.NEGATIVE_INFINITY;
  private sweepPageSplits: number = 0;
  private sweepAfterRebuild: boolean = false;
  private orderedSplitRate: number | null = null;
  private usageTime: number = 0;
  private readonly signatures: WeakMap<HsDefaultShader, ShaderSignature> = new WeakMap();
  private readonly sourceIds: Map<string, number> = new Map();
  private readonly storageIds: WeakMap<object, number> = new WeakMap();
  private nextStorageId: number = 0;
  private readonly shaders: Map<string, HsDefaultShader> = new Map();
  private readonly data: Float32Array = new Float32Array(HS_MESH_BATCH_WINDOW_OBJECTS * 20);
  private readonly counts: Int32Array = new Int32Array(MAX_OBJECTS);
  private readonly offsets: Int32Array = new Int32Array(MAX_OBJECTS);
  private dataTexture: WebGLTexture | null = null;
  private readonly dataTextures: WebGLTexture[] = [];
  private textureCursor: number = 0;
  private windowObjects: HsGraphicObject[] | null = null;
  private windowStart: number = 0;
  private windowEnd: number = 0;
  private readonly cachedWindows: Map<HsGraphicObject, TransformWindow> = new Map();
  private readonly textureWindows: (TransformWindow | undefined)[] = [];
  private previousCameraPosition: number[] | null = null;
  private firstWindow: boolean = true;
  private cacheThisDraw: boolean = true;
  private unavailable: boolean = false;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.extension = gl.getExtension('WEBGL_multi_draw') as MultiDrawExtension | null;
  }

  public beginDraw(): void {
    this.windowObjects = null;
    this.firstWindow = true;
    this.usageTime = performance.now();
  }

  /** Starts a preparation sweep over the current render lists; every packed range it reaches is marked as used. */
  public beginPreparationSweep(): void {
    this.usageTime = performance.now();
    this.sweepStartedAt = this.usageTime;
    this.sweepPageSplits = 0;
    // A sweep that fills an empty atlas packs in render order, like the sweep after a rebuild.
    this.sweepAfterRebuild ||= this.pages.length === 0;
  }

  /**
   * Rebuilds the atlas after a completed preparation sweep when its packing no longer fits the swept lists:
   * ranges the sweep did not reach (other views or disposed geometry) fill most of it, or a quarter of a full
   * atlas, or adjacent compatible meshes were split across pages far more often than render-order packing
   * would. Packing is append-only, so either way the current view's batches split. Returns true when the lists
   * must be swept again to repack them. Call only between batches: returned batches reference atlas pages.
   */
  public releaseGeometryOutsideSweep(sweptObjects: number): boolean {
    const full: boolean = this.compactionRequested;
    this.compactionRequested = false;
    const splitRate: number = this.sweepPageSplits / Math.max(1, sweptObjects);
    if (this.sweepAfterRebuild) {
      // The sweep that repacked a rebuilt atlas shows what render-order packing achieves; never rebuild again here.
      this.sweepAfterRebuild = false;
      this.orderedSplitRate = splitRate;
      return false;
    }
    const splitLimit: number =
      this.orderedSplitRate === null
        ? this.pages.length * SWEEP_PAGE_SPLITS_PER_PAGE
        : this.orderedSplitRate * SWEEP_PAGE_SPLIT_GROWTH * sweptObjects;
    const fragmented: boolean = this.sweepPageSplits > Math.max(MIN_SWEEP_PAGE_SPLITS, splitLimit);
    const rebuilt: boolean =
      fragmented ||
      ((full || this.pages.length >= MIN_SWEEP_REBUILD_PAGES) &&
        this.isReclaimable(
          full ? MIN_RECLAIMABLE_ATLAS_FRACTION : MIN_SWEEP_RECLAIMABLE_FRACTION,
          (range: GeometryRange) => range.usedAt < this.sweepStartedAt
        ));
    if (rebuilt) {
      this.releasePages();
      this.revision++;
      this.sweepAfterRebuild = true;
    }
    return rebuilt;
  }

  /**
   * Rebuilds the packed geometry atlas when it is full and largely occupied by disposed or undrawn geometry.
   * For renderers without preparation sweeps. Call only between batches; drawn geometry is repacked lazily.
   */
  public releaseUnusedGeometry(): boolean {
    if (!this.compactionRequested) {
      return false;
    }
    this.compactionRequested = false;
    const now: number = performance.now();
    if (now - this.lastCompactionCheckAt < MIN_COMPACTION_INTERVAL_MS) {
      return false;
    }
    this.lastCompactionCheckAt = now;
    if (
      !this.isReclaimable(
        MIN_RECLAIMABLE_ATLAS_FRACTION,
        (range: GeometryRange) => this.usageTime - range.usedAt > UNUSED_RANGE_MS
      )
    ) {
      return false;
    }
    this.releasePages();
    this.revision++;
    return true;
  }

  public getBatch(
    objects: HsGraphicObject[],
    start: number,
    transparent: boolean,
    limit: number = MAX_OBJECTS,
    prepare: boolean = true
  ): HsMeshBatch | null {
    if (this.unavailable) {
      return null;
    }
    const first: HsGraphicObject = objects[start];
    const shader: HsDefaultShader | null = this.getShader(first);
    if (
      !(first.geometry instanceof HsIndexedGeometry) ||
      shader === null ||
      first.geometry.indexArrayLength > MAX_INDICES
    ) {
      return null;
    }
    const canMultiDraw: boolean = this.extension !== null && first.geometry.batchingIndices !== undefined;
    // Multi-draw submits its draws in order, so sorted transparent runs with mixed geometry keep their blending order.
    const mode: HsMeshBatchMode =
      transparent && (!canMultiDraw || objects[start + 1]?.geometry === first.geometry) ? 'instances' : 'multi-draw';
    if (mode === 'multi-draw' && !canMultiDraw) {
      return null;
    }
    const end: number = Math.min(objects.length, start + MAX_OBJECTS, start + Math.max(1, limit));
    const signature: number = this.getSignature(shader);
    let page: GeometryPage | null = null;
    let count: number = 1;
    let indices: number = first.geometry.indexArrayLength;
    for (let index: number = start + 1; index < end; index++) {
      const object: HsGraphicObject = objects[index];
      const nextShader: HsDefaultShader | null = this.getShader(object);
      if (
        !(object.geometry instanceof HsIndexedGeometry) ||
        nextShader === null ||
        nextShader.clippingStorage !== shader.clippingStorage ||
        nextShader.hasTransparency !== shader.hasTransparency ||
        this.getSignature(nextShader) !== signature ||
        indices + object.geometry.indexArrayLength > MAX_INDICES
      ) {
        break;
      }
      if (mode === 'instances') {
        if (object.geometry !== first.geometry) {
          break;
        }
      } else {
        if (object.geometry.batchingIndices === undefined) {
          break;
        }
        const firstRange: GeometryRange | null = this.getRange(first.geometry, prepare);
        const range: GeometryRange | null = this.getRange(object.geometry, prepare);
        if (firstRange === null || range === null || range.page !== firstRange.page) {
          if (prepare && firstRange !== null && range !== null) {
            this.sweepPageSplits++;
          }
          break;
        }
        page = firstRange.page;
      }
      indices += object.geometry.indexArrayLength;
      count++;
    }
    if (count < 2) {
      return null;
    }
    return { count, mode, shader, page };
  }

  public getBatchShader(batch: HsMeshBatch, prepare: boolean = false): HsDefaultShader {
    const key: string = `${batch.mode}:${this.getSignature(batch.shader)}`;
    let shader: HsDefaultShader | undefined = this.shaders.get(key);
    if (shader === undefined) {
      const factory: HsDefaultShader['makeMeshBatchShader'] = batch.shader.makeMeshBatchShader;
      if (factory === null) {
        throw new Error('Mesh batching requires a generated mesh shader.');
      }
      shader = factory(batch.mode);
      if (prepare) {
        shader.prepare(this.gl);
      } else {
        shader.update(this.gl);
      }
      this.shaders.set(key, shader);
    }
    if (!prepare) {
      shader.isReady(this.gl, true);
    }
    shader.clippingStorage = batch.shader.clippingStorage;
    shader.lightStorage = batch.shader.lightStorage;
    return shader;
  }

  public getPreparedBatchShader(batch: HsMeshBatch): HsDefaultShader | null {
    const shader: HsDefaultShader | undefined = this.shaders.get(`${batch.mode}:${this.getSignature(batch.shader)}`);
    if (shader === undefined || !shader.isReady(this.gl)) {
      return null;
    }
    shader.clippingStorage = batch.shader.clippingStorage;
    shader.lightStorage = batch.shader.lightStorage;
    return shader;
  }

  public preparationComplete(deadline: number): boolean {
    for (const shader of this.shaders.values()) {
      if (performance.now() >= deadline || !shader.isReady(this.gl)) {
        return false;
      }
    }
    return true;
  }

  public draw(batch: HsMeshBatch, objects: HsGraphicObject[], start: number, camera: HsCamera): number | null {
    const shader: HsDefaultShader = this.getBatchShader(batch);
    if (!this.prepareData(objects, start, batch.count, camera)) {
      return null;
    }
    this.gl.activeTexture(this.gl.TEXTURE0);
    this.gl.bindTexture(this.gl.TEXTURE_2D, this.dataTexture);
    shader.uniform1i(this.gl, 'u_batchData', 0);
    shader.uniform1i(this.gl, 'u_batchBase', start - this.windowStart);
    let triangles: number = 0;
    for (let index: number = 0; index < batch.count; index++) {
      const object: HsGraphicObject = objects[start + index];
      if (!(object.geometry instanceof HsIndexedGeometry)) {
        throw new Error('Mesh batches require indexed geometry.');
      }
      triangles += object.geometry.indexArrayLength / 3;
      if (batch.mode === 'multi-draw') {
        const range: GeometryRange | null = this.getRange(object.geometry, false);
        if (range === null) {
          throw new Error('Missing packed geometry for a multi-draw batch.');
        }
        this.counts[index] = object.geometry.indexArrayLength;
        this.offsets[index] = range.offset;
      }
    }
    if (batch.mode === 'instances') {
      const geometry: HsGeometry = objects[start].geometry;
      if (!(geometry instanceof HsIndexedGeometry)) {
        throw new Error('Mesh batches require indexed geometry.');
      }
      this.drawInstances(shader, geometry, batch.count);
    } else {
      this.drawMulti(shader, batch);
    }
    return triangles;
  }

  public dispose(): void {
    this.releaseResources();
  }

  private releasePages(): void {
    for (const page of this.pages) {
      this.gl.deleteBuffer(page.positions);
      this.gl.deleteBuffer(page.normals);
      this.gl.deleteBuffer(page.indices);
    }
    this.pages.length = 0;
    this.ranges = new WeakMap();
    this.packedGeometries.length = 0;
    this.compactionRequested = false;
  }

  private releaseResources(): void {
    this.releasePages();
    for (const shader of this.shaders.values()) {
      shader.deleteProgram(this.gl);
    }
    this.shaders.clear();
    for (const texture of this.dataTextures) {
      this.gl.deleteTexture(texture);
    }
    this.dataTextures.length = 0;
    this.dataTexture = null;
    this.cachedWindows.clear();
    this.textureWindows.length = 0;
  }

  private getShader(object: HsGraphicObject): HsDefaultShader | null {
    if (
      object.constructor !== HsGraphicObject ||
      !object.visible ||
      object.isDeleted ||
      object.occluded ||
      !(object.geometry instanceof HsIndexedGeometry) ||
      object.geometry instanceof HsTexturedIndexedGeometry ||
      object.geometry.isDeleted ||
      object.geometry.geometryType !== HsGeometryType.Triangles
    ) {
      return null;
    }
    const shader: HsShader = object.material.getShader();
    return shader instanceof HsDefaultShader && shader.makeMeshBatchShader !== null ? shader : null;
  }

  private getSignature(shader: HsDefaultShader): number {
    const cached: ShaderSignature | undefined = this.signatures.get(shader);
    if (
      cached?.clippingStorage === shader.clippingStorage &&
      cached.lightStorage === shader.lightStorage &&
      cached.vertexCode === shader.makeVertexCode &&
      cached.fragmentCode === shader.makeFragmentCode
    ) {
      return cached.id;
    }
    // Static lights are baked into the generated code; only dynamic lights depend on the storage instance.
    const storages: object[] = HsShaderLightFunctions.hasDynamicLights(shader.lightStorage.lights)
      ? [shader.clippingStorage, shader.lightStorage]
      : [shader.clippingStorage];
    const ids: number[] = storages.map((storage: object) => {
      let id: number | undefined = this.storageIds.get(storage);
      if (id === undefined) {
        id = this.nextStorageId++;
        this.storageIds.set(storage, id);
      }
      return id;
    });
    const source: string = `${ids.join(':')}:${shader.makeVertexCode()}\n${shader.makeFragmentCode()}`;
    let signature: number | undefined = this.sourceIds.get(source);
    if (signature === undefined) {
      signature = this.sourceIds.size;
      this.sourceIds.set(source, signature);
    }
    this.signatures.set(shader, {
      id: signature,
      clippingStorage: shader.clippingStorage,
      lightStorage: shader.lightStorage,
      vertexCode: shader.makeVertexCode,
      fragmentCode: shader.makeFragmentCode,
    });
    return signature;
  }

  private getRange(geometry: HsIndexedGeometry, prepare: boolean): GeometryRange | null {
    if (this.unavailable) {
      return null;
    }
    const existing: GeometryRange | undefined = this.ranges.get(geometry);
    if (existing !== undefined) {
      existing.usedAt = this.usageTime;
      return existing;
    }
    if (!prepare) {
      return null;
    }
    const indices: Uint32Array | undefined = geometry.batchingIndices;
    const vertices: number = geometry.positionArrayLength / 3;
    if (indices === undefined || vertices > PAGE_VERTICES || indices.length > PAGE_INDICES) {
      return null;
    }
    let page: GeometryPage | null = this.pages[this.pages.length - 1] ?? null;
    if (
      page === null ||
      page.verticesUsed + vertices > PAGE_VERTICES ||
      page.indicesUsed + indices.length > PAGE_INDICES
    ) {
      if ((this.pages.length + 1) * PAGE_BYTES > MAX_ATLAS_BYTES) {
        this.compactionRequested = true;
        return null;
      }
      page = this.createPage();
      if (page === null) {
        return null;
      }
      this.pages.push(page);
    }
    const gl: WebGL2RenderingContext = this.gl;
    const vertexBytes: number = geometry.positionArrayLength * 4;
    const destination: number = page.verticesUsed * 12;
    gl.bindBuffer(gl.COPY_READ_BUFFER, geometry.buffer);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, page.positions);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, 0, destination, vertexBytes);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, page.normals);
    gl.copyBufferSubData(gl.COPY_READ_BUFFER, gl.COPY_WRITE_BUFFER, vertexBytes, destination, vertexBytes);
    const shifted: Uint32Array = new Uint32Array(indices.length);
    for (let index: number = 0; index < indices.length; index++) {
      shifted[index] = indices[index] + page.verticesUsed;
    }
    const range: GeometryRange = { page, offset: page.indicesUsed * 4, vertices, usedAt: this.usageTime };
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, page.indices);
    gl.bufferSubData(gl.COPY_WRITE_BUFFER, range.offset, shifted);
    gl.bindBuffer(gl.COPY_READ_BUFFER, null);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
    page.verticesUsed += vertices;
    page.indicesUsed += indices.length;
    this.ranges.set(geometry, range);
    this.packedGeometries.push(geometry);
    this.revision++;
    return range;
  }

  private isReclaimable(fraction: number, unused: (range: GeometryRange) => boolean): boolean {
    let usedVertices: number = 0;
    let reclaimableVertices: number = 0;
    for (const geometry of this.packedGeometries) {
      const range: GeometryRange | undefined = this.ranges.get(geometry);
      if (range === undefined) {
        continue;
      }
      usedVertices += range.vertices;
      if (geometry.isDeleted || unused(range)) {
        reclaimableVertices += range.vertices;
      }
    }
    return usedVertices > 0 && reclaimableVertices >= usedVertices * fraction;
  }

  private createPage(): GeometryPage | null {
    const positions: WebGLBuffer | null = this.gl.createBuffer();
    const normals: WebGLBuffer | null = this.gl.createBuffer();
    const indices: WebGLBuffer | null = this.gl.createBuffer();
    if (positions === null || normals === null || indices === null) {
      this.gl.deleteBuffer(positions);
      this.gl.deleteBuffer(normals);
      this.gl.deleteBuffer(indices);
      console.warn('Mesh batching disabled because geometry buffers could not be allocated.');
      this.unavailable = true;
      this.releaseResources();
      return null;
    }
    // WebGL distinguishes index buffers from vertex buffers by their first binding.
    this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, indices);
    this.clearPendingErrors();
    try {
      for (const [buffer, bytes] of [
        [positions, PAGE_VERTICES * 12],
        [normals, PAGE_VERTICES * 12],
        [indices, PAGE_INDICES * 4],
      ] as const) {
        this.gl.bindBuffer(this.gl.COPY_WRITE_BUFFER, buffer);
        this.gl.bufferData(this.gl.COPY_WRITE_BUFFER, bytes, this.gl.STATIC_DRAW);
      }
      const error: number = this.gl.getError();
      if (error !== this.gl.NO_ERROR) {
        console.warn(`Mesh batching disabled after geometry allocation failed (WebGL error ${error}).`);
        this.gl.deleteBuffer(positions);
        this.gl.deleteBuffer(normals);
        this.gl.deleteBuffer(indices);
        this.unavailable = true;
        this.releaseResources();
        return null;
      }
    } catch (error) {
      this.gl.deleteBuffer(positions);
      this.gl.deleteBuffer(normals);
      this.gl.deleteBuffer(indices);
      console.warn('Mesh batching disabled after geometry allocation failed.', error);
      this.unavailable = true;
      this.releaseResources();
      return null;
    } finally {
      this.gl.bindBuffer(this.gl.COPY_WRITE_BUFFER, null);
    }
    return { positions, normals, indices, verticesUsed: 0, indicesUsed: 0 };
  }

  /**
   * getError reports the oldest unread error of the whole context, so an earlier failure elsewhere (for example
   * INVALID_FRAMEBUFFER_OPERATION) would otherwise be blamed on the next allocation and disable batching for good.
   */
  private clearPendingErrors(): void {
    for (let attempt: number = 0; attempt < MAX_PENDING_GL_ERRORS; attempt++) {
      if (this.gl.getError() === this.gl.NO_ERROR) {
        return;
      }
    }
  }

  private bindVertices(shader: HsDefaultShader, positions: WebGLBuffer, normalsOffset: number): void {
    const gl: WebGL2RenderingContext = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, positions);
    gl.vertexAttribPointer(shader.getAttribute('a_positions'), 3, gl.FLOAT, false, 12, 0);
    const normalLocation: number = shader.getAttribute('a_normals');
    if (normalLocation !== -1) {
      gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, true, 12, normalsOffset);
    }
  }

  private drawInstances(shader: HsDefaultShader, geometry: HsIndexedGeometry, count: number): void {
    const gl: WebGL2RenderingContext = this.gl;
    this.bindVertices(shader, geometry.buffer, geometry.positionArrayLength * 4);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, geometry.indexBuffer);
    gl.drawElementsInstanced(gl.TRIANGLES, geometry.indexArrayLength, gl.UNSIGNED_INT, 0, count);
  }

  private prepareData(objects: HsGraphicObject[], start: number, count: number, camera: HsCamera): boolean {
    if (this.windowObjects === objects && start >= this.windowStart && start + count <= this.windowEnd) {
      return true;
    }
    const cameraPosition: number[] = camera.worldMatrix.getPositionArray();
    if (this.firstWindow) {
      this.cacheThisDraw =
        this.previousCameraPosition === null ||
        this.previousCameraPosition.every((value: number, index: number) => value === cameraPosition[index]);
      this.previousCameraPosition = cameraPosition;
      this.firstWindow = false;
    }
    const end: number = Math.min(objects.length, start + HS_MESH_BATCH_WINDOW_OBJECTS);
    const cached: TransformWindow | undefined = this.cachedWindows.get(objects[start]);
    if (
      this.reuseTransformData &&
      cached !== undefined &&
      this.matchesWindow(cached, objects, start, end, cameraPosition)
    ) {
      this.windowObjects = objects;
      this.windowStart = start;
      this.windowEnd = end;
      this.dataTexture = cached.texture;
      return true;
    }
    for (let index: number = start; index < end; index++) {
      const object: HsGraphicObject = objects[index];
      const offset: number = (index - start) * 20;
      this.data.set(object.worldMatrix.values, offset);
      // Subtract in double precision before writing camera-relative float texels.
      this.data[offset + 12] = object.worldMatrix.values[12] - cameraPosition[0];
      this.data[offset + 13] = object.worldMatrix.values[13] - cameraPosition[1];
      this.data[offset + 14] = object.worldMatrix.values[14] - cameraPosition[2];
      this.data.set(object.material.color.values, offset + 16);
    }
    const gl: WebGL2RenderingContext = this.gl;
    const width: number = HS_MESH_BATCH_OBJECTS_PER_ROW * HS_MESH_BATCH_PIXELS_PER_OBJECT;
    const height: number = Math.ceil((end - start) / HS_MESH_BATCH_OBJECTS_PER_ROW);
    gl.activeTexture(gl.TEXTURE0);
    // A bounded ring avoids rewriting transforms still consumed by queued GPU draws.
    const textureIndex: number = this.textureCursor % MAX_DATA_TEXTURES;
    let texture: WebGLTexture | undefined = this.dataTextures[textureIndex];
    if (texture === undefined) {
      const allocated: WebGLTexture | null = gl.createTexture();
      if (allocated === null) {
        this.disableAfterTransformAllocationFailure('mesh batch transform texture could not be allocated');
        return false;
      }
      texture = allocated;
      this.clearPendingErrors();
      try {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texStorage2D(
          gl.TEXTURE_2D,
          1,
          gl.RGBA32F,
          width,
          HS_MESH_BATCH_WINDOW_OBJECTS / HS_MESH_BATCH_OBJECTS_PER_ROW
        );
        const error: number = gl.getError();
        if (error === gl.NO_ERROR) {
          this.dataTextures[textureIndex] = texture;
        } else {
          this.disableAfterTransformAllocationFailure(`WebGL error ${error}`, texture);
          return false;
        }
      } catch (error) {
        this.disableAfterTransformAllocationFailure('an exception was thrown', texture, error);
        return false;
      }
    } else {
      gl.bindTexture(gl.TEXTURE_2D, texture);
    }
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, width, height, gl.RGBA, gl.FLOAT, this.data);
    const previous: TransformWindow | undefined = this.textureWindows[textureIndex];
    if (previous !== undefined && this.cachedWindows.get(previous.objects[0]) === previous) {
      this.cachedWindows.delete(previous.objects[0]);
    }
    this.textureWindows[textureIndex] = undefined;
    if (this.reuseTransformData && this.cacheThisDraw) {
      const windowObjects: HsGraphicObject[] = objects.slice(start, end);
      const values: Float64Array = new Float64Array(windowObjects.length * 20);
      for (let index: number = 0; index < windowObjects.length; index++) {
        values.set(windowObjects[index].worldMatrix.values, index * 20);
        values.set(windowObjects[index].material.color.values, index * 20 + 16);
      }
      const window: TransformWindow = { objects: windowObjects, values, camera: cameraPosition, texture };
      this.cachedWindows.set(objects[start], window);
      this.textureWindows[textureIndex] = window;
    }
    this.windowObjects = objects;
    this.dataTexture = texture;
    this.textureCursor++;
    this.windowStart = start;
    this.windowEnd = end;
    return true;
  }

  private disableAfterTransformAllocationFailure(
    reason: string,
    texture: WebGLTexture | undefined = undefined,
    error: unknown = undefined
  ): void {
    if (texture !== undefined) {
      this.gl.deleteTexture(texture);
    }
    console.warn(`Mesh batching disabled after transform allocation failed: ${reason}.`, error);
    this.unavailable = true;
    this.releaseResources();
  }

  private matchesWindow(
    window: TransformWindow,
    objects: HsGraphicObject[],
    start: number,
    end: number,
    camera: number[]
  ): boolean {
    if (
      window.objects.length !== end - start ||
      window.camera.some((value: number, index: number) => value !== camera[index])
    ) {
      return false;
    }
    for (let index: number = start; index < end; index++) {
      const object: HsGraphicObject = objects[index];
      const slot: number = index - start;
      if (window.objects[slot] !== object) return false;
      for (let component: number = 0; component < 16; component++) {
        if (window.values[slot * 20 + component] !== object.worldMatrix.values[component]) return false;
      }
      const color: number[] = object.material.color.values;
      for (let component: number = 0; component < color.length; component++) {
        if (window.values[slot * 20 + 16 + component] !== color[component]) return false;
      }
    }
    return true;
  }

  private drawMulti(shader: HsDefaultShader, batch: HsMeshBatch): void {
    const gl: WebGL2RenderingContext = this.gl;
    const page: GeometryPage | null = batch.page;
    if (page === null || this.extension === null) {
      throw new Error('Multi-draw requires packed geometry and WEBGL_multi_draw.');
    }
    this.bindVertices(shader, page.positions, 0);
    const normalLocation: number = shader.getAttribute('a_normals');
    if (normalLocation !== -1) {
      gl.bindBuffer(gl.ARRAY_BUFFER, page.normals);
      gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, true, 12, 0);
    }
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, page.indices);
    this.extension.multiDrawElementsWEBGL(gl.TRIANGLES, this.counts, 0, gl.UNSIGNED_INT, this.offsets, 0, batch.count);
  }
}
